import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
} from "@angular/core";
import { AgEditorComponent } from "@bfx/ag-grid-angular";
import { BfxHedgeOrderMonitorUtilService } from "app/bfx-hedge-order-monitor/services/bfx-hedge-order-monitor-util.service";
import {
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
  SpinType,
  UpdateSourceType,
  ZERO,
} from "shared/base-components/number-spinner.component";
import { BigNumber, RoundToMillionsType } from "shared/cubiq-model-multi-asset";
import { CcyPromiseLookupService } from "shared/cubiq-services";
import {
  AMOUNT_VALUE_ACCESSOR,
  BfxAmountSpinnerComponent,
} from "../../bfx-interface-controls/bfx-amount-spinner.component";
import { BfxFieldNumberDirective } from "../../../shared/base-components/bfx-field-number";
import { BfxInputDirective } from "../../../shared/base-components/bfx-input";
import { BfxAmountHelper } from "shared/helper-services/bfx-amount-helper";

@Component({
  selector: "df-amount-spinner",
  templateUrl: "../../../shared/base-components/html/number-spinner.html",
  providers: [AMOUNT_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DfAmountSpinner
  extends BfxAmountSpinnerComponent
  implements AgEditorComponent, AfterViewInit
{
  private partFillEnabled: boolean;
  protected isRowSelected: boolean;
  protected roundToMillion: RoundToMillionsType = null;
  private currentData: any;

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
    currencyPromiseService: CcyPromiseLookupService,
    protected hedgeOrderMonitorUtilService: BfxHedgeOrderMonitorUtilService,
    el: ElementRef,
  ) {
    super(injector, inputDirective, fieldDirective, currencyPromiseService, el);
    this.emptyValue = "-";
    this.size = "sm";
  }

  getValue(): any {
    return this.data.value;
  }

  protected override formatNumber(value: BigNumber, formatAsFocused: boolean): string {
    let val: BigNumber = this.checkRoundToMillion(value);
    if (val != null && val.isNegative()) {
      val = ZERO;
    }

    return super.formatNumber(value, formatAsFocused);
  }

  protected checkRoundToMillion(value: BigNumber): BigNumber {
    if (value != null && this.roundToMillion) {
      value = this.hedgeOrderMonitorUtilService.roundToMillionType(value, this.roundToMillion);
    }

    return value;
  }

  agInit(params: any): void {
    this.min = 0;
    this.keyboardMultipliersEnabled = true;
    this.spinType = SpinType.RELATIVE;
    this.maxPrecision = 2;
    this.minPrecision = 2;
    this.isRowSelected = params.node.isSelected();
    this.partFillEnabled = params.node.data.partFillEnabled;
    this.currentData = params.node.data;

    if (params.context != null && params.context.isCellEditingEnabled) {
      this.partFillEnabled = this.isRowSelected = params.context.isCellEditingEnabled(params);
    }

    if (params.context != null && params.context.shouldRoundToMillions) {
      this.roundToMillion = params.context.shouldRoundToMillions(params);
      if (this.roundToMillion) {
        this.spinType = SpinType.CONSTANT;
        this.step = 1e6;
      }
    }

    if (params.context != null && params.context.getCurrency != null) {
      this.currency = params.context.getCurrency(params);
    } else {
      this.currency = params.node.data.dealtCcy;
    }
    this.updateConfiguration();

    if (!this.partFillEnabled) {
      return;
    }

    this.updateValue(new BigNumber(0), UpdateSourceType.CONFIG_CHANGE);

    if (params.value) {
      let amt = params.value;
      let maxAmt = params.node.data.remainingAmount;
      if (amt != "-") {
        this.writeValue(amt);
        if (maxAmt != null) {
          this.max = maxAmt;
        }
      } else {
        this.data.textValue = "-";
      }
    }
  }

  isCancelBeforeStart?(): boolean {
    return !this.partFillEnabled || !this.isRowSelected;
  }

  isCancelAfterEnd?(): boolean {
    return !this.partFillEnabled || !this.isRowSelected;
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.focusInput();
    });
  }

  protected override updateValue(newValue: BigNumber, updateSource: UpdateSourceType): boolean {
    const roundedValue = this.checkRoundToMillion(newValue);
    if (this.currentData.vwapUpdateSubject) {
      this.currentData.vwapUpdateSubject.next();
    }
    return super.updateValue(roundedValue, updateSource);
  }

  /**
   * Capture and apply any special keyboard multipliers if configured.
   *
   * We need to override the impl in parent class as it uses the stored value - when roundToMillions is enabled,
   * this stored value is rounded value of displayed text (FXBFXOM-8485) so we need to get back to this original
   * text value before we apply the keyboard scaling, then reapply the rounding
   *
   * @param event Keyboard Event to process
   * @returns true if key processed
   */
  protected override applyKeyEventToAmount(event: KeyboardEvent): boolean {
    if (!this.roundToMillion) {
      return super.applyKeyEventToAmount(event);
    }

    if (this.data.value == null || !this.config.keyboardMultipliersEnabled) {
      return false;
    }

    if (event.key && event.key.length == 1) {
      let multiplier: number = BfxAmountHelper.getAmtMultiplierForChar(event.key);
      if (multiplier > 1) {
        // Here we reprocess the actual text rather than the stored value
        const element: HTMLInputElement = this.inputField.nativeElement;
        const numValue = this.createValueFromText(element.value) ?? ZERO;
        const newValue = numValue.times(multiplier);
        this.updateValue(newValue, UpdateSourceType.USER_INPUT);
        this.formatInputValue();
        return true;
      }
    }
    return false;
  }
}
